import React from "react";
import { Link } from "react-router-dom";
import { withToastManager } from "react-toast-notifications";
import api from "../../../Environment";
import ToastDemo from "../../Helper/toaster";
import Helper from "../../Helper/helper";
import { translate, t } from "react-multi-lang";
import BrandRating from "../../Rating/BrandRating";
import { formatRatingDisplay } from "../../Rating/formatRatingDisplay";
import BrandAssetManager from "../../../branding/BrandAssetManager";
import { resolveVideoContextualActions } from "../../Helper/videoContextualActions";
import VideoDescriptionBody from "./VideoDescriptionBody";
import VideoAccessBadge from "../../Helper/VideoAccessBadge";

const DATE_OPTIONS = {
  year: "numeric",
  month: "short",
};

/**
 * Slot size for Brand Package ratings on Video Details.
 * Presentation-only: container footprint via --brand-rating-size (SVG/PNG identical).
 * Was 28px; doubled to 56px for clearer dove icons without asset edits.
 */
const VIDEO_DETAILS_RATING_SIZE = 56;

/** Parse a community/display average; preserves explicit 0. */
function parseRatingNumber(value, fallback) {
  if (value == null || value === "") {
    return fallback != null ? fallback : 0;
  }
  const n = Number(value);
  return isFinite(n) ? n : fallback != null ? fallback : 0;
}

/**
 * True when the viewer has a stored rating (0–5 inclusive).
 * null/undefined/"" = never rated (no row). Explicit 0 is a real vote.
 */
function hasUserRatingValue(value) {
  if (value == null || value === "") return false;
  const n = Number(value);
  return isFinite(n) && n >= 0 && n <= 5;
}

/** Parse user rating for display; unset (no row) → 0 with hasUserRating false. */
function parseUserRating(value) {
  if (!hasUserRatingValue(value)) return 0;
  const n = Number(value);
  return isFinite(n) && n >= 0 && n <= 5 ? n : 0;
}

/** VMS field as display text. Empty/missing → "" — never substitute another field. */
function videoFieldText(value) {
  if (value == null) return "";
  return String(value);
}

class VideoOverView extends Helper {
  state = {
    inputData: {
      admin_video_id: this.props.videoDetailsFirst.admin_video_id,
    },
    wishlistApiCall: false,
    wishlistResponse: {
      wishlist_id: null,
    },
    redirect: false,
    redirectPPV: false,
    redirectPaymentOption: false,
    playButtonClicked: false,
    wishlistStatusCheck: 0,
    displayRating: parseRatingNumber(this.props.videoDetailsFirst.ratings, 0),
    userRating: parseUserRating(this.props.videoDetailsFirst.user_rating),
    hasUserRating: hasUserRatingValue(this.props.videoDetailsFirst.user_rating),
    ratingsCount: Number(this.props.videoDetailsFirst.ratings_count) || 0,
    ratingSaving: false,
    overviewExpanded: false,
  };
  componentDidMount() {
    this.setState({ playButtonClicked: false });

    let wishlistStatusCheck = 0;
    if (this.props.videoDetailsFirst.wishlist_status == 1) {
      wishlistStatusCheck = 1;
    } else {
      wishlistStatusCheck = 0;
    }
    this.setState({
      wishlistStatusCheck,
      displayRating: parseRatingNumber(this.props.videoDetailsFirst.ratings, 0),
      userRating: parseUserRating(this.props.videoDetailsFirst.user_rating),
      hasUserRating: hasUserRatingValue(this.props.videoDetailsFirst.user_rating),
      ratingsCount: Number(this.props.videoDetailsFirst.ratings_count) || 0,
    });
  }

  componentDidUpdate(prevProps) {
    const prevId =
      prevProps.videoDetailsFirst && prevProps.videoDetailsFirst.admin_video_id;
    const nextId =
      this.props.videoDetailsFirst &&
      this.props.videoDetailsFirst.admin_video_id;
    if (prevId !== nextId && this.state.overviewExpanded) {
      this.setState({ overviewExpanded: false });
      if (typeof this.props.onOverviewExpanded === "function") {
        this.props.onOverviewExpanded(false);
      }
    }
  }

  handleOverviewLoadMore = (event) => {
    if (event) event.preventDefault();
    this.setState({ overviewExpanded: true });
    if (typeof this.props.onOverviewExpanded === "function") {
      this.props.onOverviewExpanded(true);
    }
  };

  handleRatingChange = (rating) => {
    if (this.state.ratingSaving) {
      return;
    }

    // Explicit Number() — do not use || / !rating (0 is a valid vote).
    const next = Math.max(0, Math.min(5, Number(rating)));
    if (!isFinite(next)) {
      return;
    }

    const previousUserRating = this.state.userRating;
    const previousHasUserRating = this.state.hasUserRating;
    const previousDisplayRating = this.state.displayRating;
    const previousRatingsCount = this.state.ratingsCount;

    this.setState({
      ratingSaving: true,
      userRating: next,
      hasUserRating: true,
    });

    api
      .postMethod("userRating", {
        admin_video_id: this.state.inputData.admin_video_id,
        // String keeps multipart FormData from dropping numeric 0 as "empty".
        rating: String(next),
      })
      .then((response) => {
        if (response.data.success === true) {
          const data = response.data.data || {};
          const resolvedUserRating =
            data.user_rating !== undefined ? data.user_rating : next;
          ToastDemo(
            this.props.toastManager,
            response.data.message || "Rating saved",
            "success"
          );
          this.setState({
            displayRating: parseRatingNumber(
              data.ratings,
              previousDisplayRating
            ),
            userRating: parseUserRating(resolvedUserRating),
            hasUserRating: hasUserRatingValue(resolvedUserRating),
            ratingsCount:
              data.ratings_count != null
                ? Number(data.ratings_count)
                : this.state.ratingsCount,
            ratingSaving: false,
          });
        } else {
          ToastDemo(
            this.props.toastManager,
            response.data.error_messages || "Unable to save rating",
            "error"
          );
          this.setState({
            userRating: previousUserRating,
            hasUserRating: previousHasUserRating,
            displayRating: previousDisplayRating,
            ratingsCount: previousRatingsCount,
            ratingSaving: false,
          });
        }
      })
      .catch(() => {
        this.setState({
          userRating: previousUserRating,
          hasUserRating: previousHasUserRating,
          displayRating: previousDisplayRating,
          ratingsCount: previousRatingsCount,
          ratingSaving: false,
        });
      });
  };

  handleWishList = (event) => {
    event.preventDefault();

    // this.wishlistUpdate(this.state.inputData);

    api
      .postMethod("wishlists/operations", this.state.inputData)
      .then((response) => {
        if (response.data.success) {
          ToastDemo(this.props.toastManager, response.data.message, "success");
          this.setState({
            wishlistResponse: response.data,
            wishlistApiCall: true,
          });
          if (response.data.wishlist_id != null) {
            this.setState({
              wishlistStatusCheck: 1,
            });
          } else {
            this.setState({
              wishlistStatusCheck: 0,
            });
          }
        } else {
          ToastDemo(
            this.props.toastManager,
            response.data.error_messages,
            "error"
          );
        }
      });
  };

  handlePlayVideo = (event) => {
    event.preventDefault();
    this.redirectStatus(this.props.videoDetailsFirst);
    this.setState({ playButtonClicked: true });
  };
  render() {
    const { videoDetailsFirst } = this.props;

    const {
      wishlistStatusCheck,
      displayRating,
      userRating,
      hasUserRating,
    } = this.state;

    const contextualActions = resolveVideoContextualActions(videoDetailsFirst);

    if (this.state.playButtonClicked) {
      const returnToVideo = this.renderRedirectPage(
        this.props.videoDetailsFirst
      );

      if (returnToVideo != null) {
        return returnToVideo;
      }
    }
    return (
      <div className="slider-topbottom-spacing">
        <div className="overview-content">
          <h1 className="overview_video_title">{videoDetailsFirst.title}</h1>
          <h4 className="banner_video_details">
            <span className="green-clr">
              {new Date(videoDetailsFirst.publish_time).toLocaleDateString(
                "en-US",
                DATE_OPTIONS
              )}
            </span>
            <span className="grey-box">
              {videoDetailsFirst.age}
              <i className="fas fa-plus small" /> /{" "}
              {videoDetailsFirst.watch_count}{" "}
              <span className="small">Views</span>
            </span>
            <span>{videoDetailsFirst.duration}</span>
            {videoDetailsFirst.should_display_ppv == 1 ? (
              <span className="red-box">
                {videoDetailsFirst.currency} {videoDetailsFirst.ppv_amount}
              </span>
            ) : null}
            <VideoAccessBadge item={videoDetailsFirst} className="video-access-badge--detail" />
          </h4>
          <div className="brand-rating-block">
            <div className="brand-rating-row">
              <span className="brand-rating-row__label">Community Rating</span>
              <BrandRating
                ratingValue={displayRating}
                hasRating={
                  this.state.ratingsCount > 0 || displayRating > 0
                }
                readOnly
                allowFractional
                showValue={
                  this.state.ratingsCount > 0 || displayRating > 0
                }
                size={VIDEO_DETAILS_RATING_SIZE}
                aria-label={
                  this.state.ratingsCount > 0 || displayRating > 0
                    ? formatRatingDisplay(displayRating) +
                      " of 5 Rating Icons"
                    : "Community Rating"
                }
              />
            </div>
            <div className="brand-rating-row">
              <span className="brand-rating-row__label">Your Rating</span>
              <BrandRating
                ratingValue={userRating}
                hasRating={hasUserRating}
                allowFractional={false}
                showValue={hasUserRating}
                size={VIDEO_DETAILS_RATING_SIZE}
                onChange={(rating) => this.handleRatingChange(rating)}
                aria-label={
                  hasUserRating
                    ? "Your Rating, " +
                      formatRatingDisplay(userRating) +
                      " of 5. Click left of the first dove for 0, or a dove for 1 to 5."
                    : "Your Rating, not rated. Click left of the first dove for 0, or a dove for 1 to 5."
                }
              />
            </div>
          </div>
          <div
            className={
              "slider_video_text" +
              (this.state.overviewExpanded ? " slider_video_text--expanded" : "")
            }
          >
            <VideoDescriptionBody
              text={
                this.state.overviewExpanded
                  ? videoFieldText(videoDetailsFirst.description)
                  : videoFieldText(videoDetailsFirst.short_description)
              }
              linkPreview={
                this.state.overviewExpanded
                  ? videoDetailsFirst.description_link_preview || null
                  : videoDetailsFirst.short_description_link_preview || null
              }
            />
          </div>
          {!this.state.overviewExpanded &&
          videoFieldText(videoDetailsFirst.description).trim() !== "" ? (
            <button
              type="button"
              className="overview-load-more"
              onClick={this.handleOverviewLoadMore}
            >
              {t("load_more")}
            </button>
          ) : null}
          <div className="banner-btn-sec">
            <Link
              to="#"
              className="btn btn-danger btn-right-space br-0"
              onClick={this.handlePlayVideo}
            >
              <i className="fas fa-play mr-2" />
              {t("play")}
            </Link>
            <Link
              to="#"
              onClick={this.handleWishList}
              className="btn btn-outline-secondary btn-right-space"
            >
              {wishlistStatusCheck == 1 ? (
                <div>
                  <img
                    src={window.location.origin + "/images/tick.png"}
                    className="mr-2"
                    alt=""
                  />
                  {BrandAssetManager.collectionLabel()}
                </div>
              ) : (
                <div>
                  <img
                    src={window.location.origin + "/images/add.png"}
                    className="mr-2"
                    alt=""
                  />
                  {BrandAssetManager.collectionLabel()}
                </div>
              )}
            </Link>
            {contextualActions.map((action) => (
              <Link
                key={action.key}
                to={action.to}
                className="btn btn-outline-secondary btn-right-space video-contextual-action"
                aria-label={action.ariaLabel || action.label}
              >
                {action.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    );
  }
}

export default withToastManager(translate(VideoOverView));
